/**
 * ロールと権限の対応表（フロントエンド側の正）。
 *
 * API・RLS と**同じマトリクス**をここに持つ。散在する
 * `role === 'platform_admin'` の完全一致比較は必ずここを経由させる。
 * 比較を直書きするとロールを追加したときに漏れが出る（実際に
 * platform_owner / platform_editor の追加で全箇所を洗い直す羽目になった）。
 *
 * ここで隠すのは操作性のためであって、権限の担保は API と RLS にある。
 * sessionView.ts と同じく純関数のみ・依存なしで、テストで表を固定する。
 */

export type Role =
  | 'platform_owner'
  | 'platform_admin'
  | 'platform_editor'
  | 'org_admin'
  | 'org_member'

/** 運営側ロール。org_id は必ず NULL。 */
export const STAFF_ROLES: readonly Role[] = [
  'platform_owner',
  'platform_admin',
  'platform_editor',
]

/** 顧客側ロール。org_id 必須。 */
export const CUSTOMER_ROLES: readonly Role[] = ['org_admin', 'org_member']

export const ROLE_LABEL: Record<Role, string> = {
  platform_owner: '最高管理者',
  platform_admin: '管理者',
  platform_editor: '編集者',
  org_admin: '顧客管理者',
  org_member: '一般',
}

/**
 * 表示用ラベル。未知のロール文字列は生値のまま返す
 * （DB 側が先にロールを増やしても画面が壊れないように）。
 */
export const roleLabel = (r: string): string =>
  (ROLE_LABEL as Record<string, string>)[r] ?? r

export const isStaffRole = (role: string): boolean =>
  (STAFF_ROLES as readonly string[]).includes(role)

export type Permission =
  | 'content.write'
  | 'category.manage'
  | 'customer.manage'
  | 'license.manage'
  | 'logs.read'
  | 'staff.manage'
  | 'settings.email'
  | 'settings.watermark'

/**
 * 権限マトリクス（permission → 許可ロール）。
 *
 * 顧客側ロール（org_admin / org_member）はどの permission も持たない。
 * 顧客の閲覧・ダウンロード範囲はライセンスが決める（ロールと直交）。
 */
const MATRIX: Record<Permission, readonly Role[]> = {
  'content.write': ['platform_owner', 'platform_admin', 'platform_editor'],
  'category.manage': ['platform_owner', 'platform_admin'],
  'customer.manage': ['platform_owner', 'platform_admin'],
  'license.manage': ['platform_owner', 'platform_admin'],
  'logs.read': ['platform_owner', 'platform_admin'],
  'staff.manage': ['platform_owner'],
  'settings.email': ['platform_owner'],
  'settings.watermark': ['platform_owner', 'platform_admin'],
}

export const can = (role: string, permission: Permission): boolean =>
  (MATRIX[permission] as readonly string[]).includes(role)

/** 設定ページに出るタブが依存する permission。タブを足したらここも足す。 */
const SETTINGS_PERMISSIONS: readonly Permission[] = [
  'customer.manage',
  'category.manage',
  'settings.watermark',
  'settings.email',
]

/** 設定画面のタブが1つ以上見えるか（カタログのサイドバー下部に「設定」の歯車を出すかの判定）。 */
export const canAnySettings = (role: string): boolean =>
  SETTINGS_PERMISSIONS.some((p) => can(role, p))
